import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { queryKeys } from '@/lib/queryKeys'
import {
  fetchDagMetadata,
  fetchDagRows,
  patchDagRows,
  fetchDropdowns,
} from '@/api/dagTable'
import type { BatchPatchRequest } from '@/types/dag'
import { toast } from '@/stores/useToastStore'

export function useDagMetadata(
  planId: string | undefined,
  versionId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.dagTable.metadata(planId!, versionId!),
    queryFn: () => fetchDagMetadata(planId!, versionId!),
    enabled: !!planId && !!versionId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDagRows(
  planId: string | undefined,
  versionId: string | undefined,
  page: number,
  pageSize: number,
  searchQuery?: string
) {
  const offset = (page - 1) * pageSize
  return useQuery({
    queryKey: queryKeys.dagTable.rows(
      planId!,
      versionId!,
      offset,
      pageSize,
      searchQuery
    ),
    queryFn: () =>
      fetchDagRows(planId!, versionId!, offset, pageSize, searchQuery),
    enabled: !!planId && !!versionId,
    placeholderData: keepPreviousData,
  })
}

export function usePatchDagRows(
  planId: string | undefined,
  versionId: string | undefined
) {
  return useMutation({
    mutationFn: (payload: BatchPatchRequest) =>
      patchDagRows(planId!, versionId!, payload),
    onError: (error: Error) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        toast.error(
          'This line was changed by someone else. Please refresh the page to see the latest data before editing again.'
        )
        return
      }
      toast.error(`Failed to update cell: ${error.message}`)
    },
  })
}

export function useDropdownLookups() {
  return useQuery({
    queryKey: queryKeys.dropdowns.all,
    queryFn: fetchDropdowns,
    staleTime: Infinity,
  })
}
